import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Save, Loader2, Plus, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { approvalTemplateService } from '@/services/approvalTemplateService'
import { employeesService } from '@/services/employeesService'
import type {
  TaskNameItem,
  ApprovalTemplateUser,
  ApprovalTemplateLine,
  DepartmentLookup,
} from '@/api/types'

/** Local row for table editing */
interface ApprovalTemplateRow {
  lineId: number
  departmentId: number
  seqNo: number
  userId: number
  isActive: boolean
  departmentName?: string
  userName?: string
}

const DEFAULT_ROW: ApprovalTemplateRow = {
  lineId: 0,
  departmentId: 0,
  seqNo: 0,
  userId: 0,
  isActive: false,
}

const IS_ACTIVE_OPTIONS: SearchableSelectOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]

/**
 * Workflow Approval Master - detail form with Approval Task dropdown and Approval Template table
 */
export const ApprovalMasterDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [taskNames, setTaskNames] = useState<TaskNameItem[]>([])
  const [selectedTaskId, setSelectedTaskId] = useState<string>('')
  const [isUpdateMode, setIsUpdateMode] = useState(false)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesByDept, setEmployeesByDept] = useState<Record<string, ApprovalTemplateUser[]>>({})
  const [rows, setRows] = useState<ApprovalTemplateRow[]>([
    { ...DEFAULT_ROW },
    { ...DEFAULT_ROW },
    { ...DEFAULT_ROW },
  ])
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(new Set())

  // Load task names and departments on mount (use same Employee/GetDepartment API as Employee module)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [tasks, depts] = await Promise.all([
          approvalTemplateService.getTaskNames(),
          employeesService.getDepartmentLookups(),
        ])
        setTaskNames(tasks)
        setDepartments(depts ?? [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // When Approval Task is selected, load template by task ID
  useEffect(() => {
    if (!selectedTaskId || !branchId) return
    const taskId = parseInt(selectedTaskId, 10)
    if (!taskId) return

    const load = async () => {
      try {
        setLoading(true)
        const data = await approvalTemplateService.getByTaskId(taskId, branchId)
        setDeletedRowIndices(new Set())
        if (data && data.length > 0) {
          const mapped: ApprovalTemplateRow[] = data.map((line: ApprovalTemplateLine) => ({
            lineId: line.lineId,
            departmentId: line.departmentId ?? 0,
            seqNo: line.seqNo,
            userId: line.userId,
            isActive: line.isActive,
            departmentName: line.departmentName,
            userName: line.userName,
          }))
          setRows(mapped)
          setIsUpdateMode(true)
          const uniqueDeptIds = [...new Set(mapped.map((r) => r.departmentId).filter(Boolean))]
          const usersByDept: Record<string, ApprovalTemplateUser[]> = {}
          await Promise.all(
            uniqueDeptIds.map(async (deptId) => {
              const users = await approvalTemplateService.getUsers(branchId, deptId)
              usersByDept[`${branchId}-${deptId}`] = users
            })
          )
          setEmployeesByDept((prev) => ({ ...prev, ...usersByDept }))
        } else {
          setRows([{ ...DEFAULT_ROW }, { ...DEFAULT_ROW }, { ...DEFAULT_ROW }])
          setIsUpdateMode(false)
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load template')
        setRows([{ ...DEFAULT_ROW }, { ...DEFAULT_ROW }, { ...DEFAULT_ROW }])
        setIsUpdateMode(false)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [selectedTaskId, branchId])

  const taskOptions: SearchableSelectOption[] = useMemo(() => {
    return taskNames
      .filter((t) => t.id && (t.lookupNameEn || t.lookupNameAr))
      .map((t) => ({
        value: String(t.id),
        label: t.lookupNameEn || t.lookupNameAr || 'Unnamed',
      }))
  }, [taskNames])

  const departmentOptions: SearchableSelectOption[] = useMemo(() => {
    return (departments || [])
      .filter((d) => d.id && d.id > 0)
      .map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || 'Unnamed',
      }))
  }, [departments])

  const getEmployeesForRow = async (departmentId: number): Promise<ApprovalTemplateUser[]> => {
    if (!departmentId || !branchId) return []
    const key = `${branchId}-${departmentId}`
    if (employeesByDept[key]) return employeesByDept[key]
    const users = await approvalTemplateService.getUsers(branchId, departmentId)
    setEmployeesByDept((prev) => ({ ...prev, [key]: users }))
    return users
  }

  const handleTaskChange = (value: string) => {
    setSelectedTaskId(value)
  }

  const handleAddRows = (count: number) => {
    setRows((prev) => [...prev, ...Array.from({ length: count }, () => ({ ...DEFAULT_ROW }))])
  }

  const handleDeleteRow = (index: number) => {
    const row = rows[index]
    if (deletedRowIndices.has(index)) {
      setDeletedRowIndices((prev) => {
        const next = new Set(prev)
        next.delete(index)
        return next
      })
    } else {
      // For existing data (when in update mode), mark as deleted (row turns red, can be undone)
      if (isUpdateMode && (row.lineId > 0 || row.userId > 0)) {
        setDeletedRowIndices((prev) => new Set(prev).add(index))
      } else {
        // For new/unsaved rows, just remove them from the list
        setRows((prev) => {
          const next = [...prev]
          next.splice(index, 1)
          return next
        })
      }
    }
  }

  const handleRowChange = (index: number, field: keyof ApprovalTemplateRow, value: number | boolean) => {
    setRows((prev) => {
      const next = [...prev]
      const row = { ...next[index], [field]: value }
      if (field === 'departmentId') {
        row.userId = 0
        row.userName = undefined
      }
      next[index] = row
      return next
    })
  }

  const handleDepartmentChange = async (index: number, value: string) => {
    const departmentId = parseInt(value, 10) || 0
    handleRowChange(index, 'departmentId', departmentId)
    if (departmentId) {
      const users = await getEmployeesForRow(departmentId)
      setEmployeesByDept((prev) => ({
        ...prev,
        [`${branchId}-${departmentId}`]: users,
      }))
    }
  }

  const handleSave = async () => {
    if (!selectedTaskId || !branchId) {
      toast.error('Please select an Approval Task.')
      return
    }
    const taskId = parseInt(selectedTaskId, 10)
    if (!taskId) return

    try {
      setSaving(true)
      // Only include lines that have data (userId selected) or are marked for delete (existing line to remove)
      const approvalTemplates = rows
        .map((row, idx) => ({
          lineId: row.lineId,
          approvalTaskId: taskId,
          seqNo: row.seqNo,
          userId: row.userId,
          isActive: row.isActive,
          isDeleted: deletedRowIndices.has(idx),
        }))
        .filter(
          (line, idx) => {
            const isDeleted = deletedRowIndices.has(idx)
            // If it's marked deleted but we're in first-time save mode, ignore it completely
            if (isDeleted && !isUpdateMode) {
              return false
            }
            // Keep rows that have an employee selected OR are marked deleted (for update)
            return line.userId > 0 || isDeleted
          }
        )
      const payload = {
        approvalTemplates,
        action: isUpdateMode ? 'update' : 'save',
        branchId,
      }
      console.log('Approval Master Save – payload structure:', JSON.stringify(payload, null, 2))
      await approvalTemplateService.save(payload)
      await showSuccess('Saved', 'Approval template saved successfully.')
      setDeletedRowIndices(new Set())
      setIsUpdateMode(true)
      const data = await approvalTemplateService.getByTaskId(taskId, branchId)
      if (data?.length) {
        setRows(data.map((line: ApprovalTemplateLine) => ({
          lineId: line.lineId,
          departmentId: line.departmentId ?? 0,
          seqNo: line.seqNo,
          userId: line.userId,
          isActive: line.isActive,
          departmentName: line.departmentName,
          userName: line.userName,
        })))
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.workflow')}
        description="Workflow Detail"
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Approval Task *</Label>
              <SearchableSelect
                options={taskOptions}
                value={selectedTaskId}
                onValueChange={handleTaskChange}
                placeholder="Select"
                searchPlaceholder="Search task..."
                disabled={loading}
                emptyMessage="No tasks found"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="bg-gray-100 dark:bg-gray-800/50 border-b flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Approval Template</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleAddRows(1)} disabled={loading}>
              <Plus className="w-4 h-4 mr-1" />
              Add Row
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleAddRows(3)} disabled={loading}>
              <Plus className="w-4 h-4 mr-1" />
              Add 3 Rows
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleAddRows(5)} disabled={loading}>
              <Plus className="w-4 h-4 mr-1" />
              Add 5 Rows
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary min-w-[180px]">
                      Department
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary w-24">
                      SeqNo
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary min-w-[180px]">
                      Employee
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary min-w-[100px]">
                      Is Active
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary w-20">
                      Delete
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => {
                    const isDeleted = deletedRowIndices.has(index)
                    const empKey = `${branchId}-${row.departmentId}`
                    const employeeList = row.departmentId ? (employeesByDept[empKey] ?? []) : []
                    const employeeOptions: SearchableSelectOption[] = employeeList.map((u) => ({
                      value: String(u.userId ?? u.id ?? 0),
                      label: u.userName || u.fullName || u.userNameEn || u.userNameAr || 'Unnamed',
                    })).filter((o) => o.value !== '0')

                    return (
                      <TableRow
                        key={index}
                        className={`hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors ${
                          isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                        }`}
                      >
                        <TableCell className="px-4 py-2 align-middle min-w-[180px]">
                          <SearchableSelect
                            options={departmentOptions}
                            value={row.departmentId ? String(row.departmentId) : ''}
                            onValueChange={(v) => handleDepartmentChange(index, v)}
                            placeholder="Select"
                            disabled={saving || isDeleted}
                            emptyMessage="No departments"
                          />
                        </TableCell>
                        <TableCell className="px-4 py-2 align-middle w-24">
                          <Input
                            type="number"
                            min={0}
                            value={row.seqNo || ''}
                            readOnly
                            disabled={saving || isDeleted}
                            className="w-full min-w-0 bg-muted/50 cursor-not-allowed"
                          />
                        </TableCell>
                        <TableCell className="px-4 py-2 align-middle min-w-[180px]">
                          <SearchableSelect
                            options={employeeOptions}
                            value={row.userId ? String(row.userId) : ''}
                            onValueChange={(v) => handleRowChange(index, 'userId', parseInt(v, 10) || 0)}
                            placeholder="Select"
                            disabled={saving || isDeleted || !row.departmentId}
                            emptyMessage="Select department first"
                          />
                        </TableCell>
                        <TableCell className="px-4 py-2 align-middle min-w-[100px]">
                          <SearchableSelect
                            options={IS_ACTIVE_OPTIONS}
                            value={row.isActive ? 'true' : 'false'}
                            onValueChange={(v) => handleRowChange(index, 'isActive', v === 'true')}
                            placeholder="No"
                            disabled={saving || isDeleted}
                            emptyMessage="No"
                          />
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle w-20">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRow(index)}
                            disabled={saving}
                            className={
                              isDeleted
                                ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                : 'text-destructive hover:bg-destructive/10'
                            }
                            title={isDeleted ? 'Undo' : 'Delete row'}
                          >
                            {isDeleted ? (
                              <span className="text-xs font-semibold">Undo</span>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || loading || !selectedTaskId}
              className="min-w-[120px]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isUpdateMode ? 'Update' : 'Save'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
